import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { systemConfigApi } from '../../api/systemConfig';

// 分页查询系统配置列表
export const fetchSystemConfigs = createAsyncThunk(
  'systemConfig/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await systemConfigApi.pageConfigs({
        page: params.page || 1,
        pageSize: params.pageSize || 10,
        configType: params.configType,
        configGroup: params.configGroup,
        configKey: params.configKey,
        ...params
      });
      
      if (response && response.code === 200) {
        const data = response.data || response;
        return {
          records: data.records || data.list || [],
          total: data.total || 0,
          current: data.current || params.page || 1,
          size: data.size || params.pageSize || 10
        };
      }
      return rejectWithValue(response?.message || '获取系统配置列表失败');
    } catch (error) {
      message.error('获取系统配置列表失败');
      return rejectWithValue(error.message || '获取系统配置列表失败');
    }
  }
);

// 获取系统配置详情
export const fetchSystemConfigDetail = createAsyncThunk(
  'systemConfig/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await systemConfigApi.getConfigById(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取系统配置详情失败');
    } catch (error) {
      message.error('获取系统配置详情失败');
      return rejectWithValue(error.message || '获取系统配置详情失败');
    }
  }
);

// 根据配置键获取配置
export const fetchSystemConfigByKey = createAsyncThunk(
  'systemConfig/fetchByKey',
  async (configKey, { rejectWithValue }) => {
    try {
      const response = await systemConfigApi.getConfigByKey(configKey);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取系统配置失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取系统配置失败');
    }
  }
);

// 批量根据配置键获取配置
export const fetchSystemConfigsByKeys = createAsyncThunk(
  'systemConfig/fetchByKeys',
  async (configKeys = [], { rejectWithValue }) => {
    try {
      const promises = configKeys.map(key => systemConfigApi.getConfigByKey(key));
      const responses = await Promise.all(promises);
      
      const configs = {};
      responses.forEach((response, index) => {
        if (response && response.code === 200 && response.data) {
          const key = configKeys[index];
          configs[key] = response.data;
        }
      });
      
      return configs;
    } catch (error) {
      return rejectWithValue(error.message || '获取系统配置失败');
    }
  }
);

// 根据配置分组获取配置列表
export const fetchSystemConfigsByGroup = createAsyncThunk(
  'systemConfig/fetchByGroup',
  async (configGroup, { rejectWithValue }) => {
    try {
      const response = await systemConfigApi.listByConfigGroup(configGroup);
      if (response && response.code === 200) {
        return response.data || [];
      }
      return rejectWithValue(response?.message || '获取系统配置列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取系统配置列表失败');
    }
  }
);

// 根据配置类型获取配置列表
export const fetchSystemConfigsByType = createAsyncThunk(
  'systemConfig/fetchByType',
  async (configType, { rejectWithValue }) => {
    try {
      const response = await systemConfigApi.listByConfigType(configType);
      if (response && response.code === 200) {
        return response.data || [];
      }
      return rejectWithValue(response?.message || '获取系统配置列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取系统配置列表失败');
    }
  }
);

// 创建系统配置
export const createSystemConfig = createAsyncThunk(
  'systemConfig/create',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await systemConfigApi.createConfig(data);
      if (response && response.code === 200) {
        message.success('创建系统配置成功');
        // 重新获取列表
        const { systemConfig } = getState();
        const { configList } = systemConfig;
        dispatch(fetchSystemConfigs({
          page: configList.current,
          pageSize: configList.size
        }));
        return response.data;
      }
      message.error(response?.message || '创建系统配置失败');
      return rejectWithValue(response?.message || '创建系统配置失败');
    } catch (error) {
      message.error('创建系统配置失败');
      return rejectWithValue(error.message || '创建系统配置失败');
    }
  }
);

// 更新系统配置
export const updateSystemConfig = createAsyncThunk(
  'systemConfig/update',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await systemConfigApi.updateConfig(id, data);
      if (response && response.code === 200) {
        message.success('更新系统配置成功');
        // 重新获取列表
        const { systemConfig } = getState();
        const { configList } = systemConfig;
        dispatch(fetchSystemConfigs({
          page: configList.current,
          pageSize: configList.size
        }));
        return response.data;
      }
      message.error(response?.message || '更新系统配置失败');
      return rejectWithValue(response?.message || '更新系统配置失败');
    } catch (error) {
      message.error('更新系统配置失败');
      return rejectWithValue(error.message || '更新系统配置失败');
    }
  }
);

// 更新配置值
export const updateSystemConfigValue = createAsyncThunk(
  'systemConfig/updateValue',
  async ({ configKey, configValue }, { rejectWithValue, dispatch }) => {
    try {
      const response = await systemConfigApi.updateConfigValue(configKey, configValue);
      if (response && response.code === 200) {
        message.success('更新配置值成功');
        // 重新获取该配置
        dispatch(fetchSystemConfigByKey(configKey));
        return { configKey, configValue };
      }
      message.error(response?.message || '更新配置值失败');
      return rejectWithValue(response?.message || '更新配置值失败');
    } catch (error) {
      message.error('更新配置值失败');
      return rejectWithValue(error.message || '更新配置值失败');
    }
  }
);

// 批量更新配置值
export const updateSystemConfigValues = createAsyncThunk(
  'systemConfig/updateValues',
  async (configUpdates = {}, { rejectWithValue, dispatch }) => {
    try {
      const promises = Object.entries(configUpdates).map(([key, value]) =>
        systemConfigApi.updateConfigValue(key, value)
      );
      
      const responses = await Promise.all(promises);
      const allSuccess = responses.every(response => response && response.code === 200);
      
      if (allSuccess) {
        message.success('更新配置成功');
        // 重新获取所有更新的配置
        const keys = Object.keys(configUpdates);
        dispatch(fetchSystemConfigsByKeys(keys));
        return configUpdates;
      }
      
      message.error('部分配置更新失败');
      return rejectWithValue('部分配置更新失败');
    } catch (error) {
      message.error('更新配置失败');
      return rejectWithValue(error.message || '更新配置失败');
    }
  }
);

// 启用或禁用配置
export const updateSystemConfigEnabled = createAsyncThunk(
  'systemConfig/updateEnabled',
  async ({ id, enabled }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await systemConfigApi.updateEnabledStatus(id, enabled);
      if (response && response.code === 200) {
        message.success(enabled ? '启用配置成功' : '禁用配置成功');
        // 重新获取列表
        const { systemConfig } = getState();
        const { configList } = systemConfig;
        dispatch(fetchSystemConfigs({
          page: configList.current,
          pageSize: configList.size
        }));
        return { id, enabled };
      }
      message.error(response?.message || '更新配置状态失败');
      return rejectWithValue(response?.message || '更新配置状态失败');
    } catch (error) {
      message.error('更新配置状态失败');
      return rejectWithValue(error.message || '更新配置状态失败');
    }
  }
);

// 删除系统配置
export const deleteSystemConfig = createAsyncThunk(
  'systemConfig/delete',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await systemConfigApi.deleteConfig(id);
      if (response && response.code === 200) {
        message.success('删除系统配置成功');
        // 重新获取列表
        const { systemConfig } = getState();
        const { configList } = systemConfig;
        dispatch(fetchSystemConfigs({
          page: configList.current,
          pageSize: configList.size
        }));
        return id;
      }
      message.error(response?.message || '删除系统配置失败');
      return rejectWithValue(response?.message || '删除系统配置失败');
    } catch (error) {
      message.error('删除系统配置失败');
      return rejectWithValue(error.message || '删除系统配置失败');
    }
  }
);

// 初始状态
const initialState = {
  configList: {
    records: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null
  },
  configDetail: {
    data: null,
    loading: false,
    error: null
  },
  configsByKey: {}, // 以 configKey 为 key 的配置对象
  configsByGroup: {
    data: [],
    loading: false,
    error: null
  },
  configsByType: {
    data: [],
    loading: false,
    error: null
  },
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建slice
const systemConfigSlice = createSlice({
  name: 'systemConfig',
  initialState,
  reducers: {
    clearConfigDetail: (state) => {
      state.configDetail = {
        data: null,
        loading: false,
        error: null
      };
    },
    clearOperationStatus: (state) => {
      state.operationStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    setPagination: (state, action) => {
      if (action.payload.current !== undefined) {
        state.configList.current = action.payload.current;
      }
      if (action.payload.size !== undefined) {
        state.configList.size = action.payload.size;
      }
    },
    clearConfigsByKey: (state) => {
      state.configsByKey = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // 分页查询列表
      .addCase(fetchSystemConfigs.pending, (state) => {
        state.configList.loading = true;
        state.configList.error = null;
      })
      .addCase(fetchSystemConfigs.fulfilled, (state, action) => {
        state.configList.loading = false;
        state.configList.records = action.payload.records;
        state.configList.total = action.payload.total;
        state.configList.current = action.payload.current;
        state.configList.size = action.payload.size;
      })
      .addCase(fetchSystemConfigs.rejected, (state, action) => {
        state.configList.loading = false;
        state.configList.error = action.payload;
      })
      // 获取详情
      .addCase(fetchSystemConfigDetail.pending, (state) => {
        state.configDetail.loading = true;
        state.configDetail.error = null;
      })
      .addCase(fetchSystemConfigDetail.fulfilled, (state, action) => {
        state.configDetail.loading = false;
        state.configDetail.data = action.payload;
      })
      .addCase(fetchSystemConfigDetail.rejected, (state, action) => {
        state.configDetail.loading = false;
        state.configDetail.error = action.payload;
      })
      // 根据键获取配置
      .addCase(fetchSystemConfigByKey.pending, (state) => {
        // 不设置全局loading，因为可能是多个配置同时加载
      })
      .addCase(fetchSystemConfigByKey.fulfilled, (state, action) => {
        if (action.payload && action.payload.configKey) {
          state.configsByKey[action.payload.configKey] = action.payload;
        }
      })
      .addCase(fetchSystemConfigByKey.rejected, (state, action) => {
        // 记录错误但不影响其他配置
      })
      // 批量根据键获取配置
      .addCase(fetchSystemConfigsByKeys.pending, (state) => {
        // 不设置全局loading
      })
      .addCase(fetchSystemConfigsByKeys.fulfilled, (state, action) => {
        state.configsByKey = {
          ...state.configsByKey,
          ...action.payload
        };
      })
      .addCase(fetchSystemConfigsByKeys.rejected, (state, action) => {
        // 记录错误
      })
      // 根据分组获取配置
      .addCase(fetchSystemConfigsByGroup.pending, (state) => {
        state.configsByGroup.loading = true;
        state.configsByGroup.error = null;
      })
      .addCase(fetchSystemConfigsByGroup.fulfilled, (state, action) => {
        state.configsByGroup.loading = false;
        state.configsByGroup.data = action.payload;
      })
      .addCase(fetchSystemConfigsByGroup.rejected, (state, action) => {
        state.configsByGroup.loading = false;
        state.configsByGroup.error = action.payload;
      })
      // 根据类型获取配置
      .addCase(fetchSystemConfigsByType.pending, (state) => {
        state.configsByType.loading = true;
        state.configsByType.error = null;
      })
      .addCase(fetchSystemConfigsByType.fulfilled, (state, action) => {
        state.configsByType.loading = false;
        state.configsByType.data = action.payload;
      })
      .addCase(fetchSystemConfigsByType.rejected, (state, action) => {
        state.configsByType.loading = false;
        state.configsByType.error = action.payload;
      })
      // 创建配置
      .addCase(createSystemConfig.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(createSystemConfig.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(createSystemConfig.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 更新配置
      .addCase(updateSystemConfig.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateSystemConfig.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateSystemConfig.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 更新配置值
      .addCase(updateSystemConfigValue.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateSystemConfigValue.fulfilled, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
        // 更新本地缓存
        if (action.payload && action.payload.configKey) {
          const config = state.configsByKey[action.payload.configKey];
          if (config) {
            config.configValue = action.payload.configValue;
          }
        }
      })
      .addCase(updateSystemConfigValue.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 批量更新配置值
      .addCase(updateSystemConfigValues.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateSystemConfigValues.fulfilled, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
        // 更新本地缓存
        if (action.payload) {
          Object.entries(action.payload).forEach(([key, value]) => {
            const config = state.configsByKey[key];
            if (config) {
              config.configValue = value;
            }
          });
        }
      })
      .addCase(updateSystemConfigValues.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 更新启用状态
      .addCase(updateSystemConfigEnabled.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateSystemConfigEnabled.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateSystemConfigEnabled.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 删除配置
      .addCase(deleteSystemConfig.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(deleteSystemConfig.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(deleteSystemConfig.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      });
  }
});

export const {
  clearConfigDetail,
  clearOperationStatus,
  setPagination,
  clearConfigsByKey
} = systemConfigSlice.actions;

export default systemConfigSlice.reducer;

